import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * GH #130: permission-based UI gating. Reads the caller's resolved permission-key
 * set from GET /me (the same ['me'] React-Query cache used elsewhere) and returns
 * a `can(feature, action)` predicate plus the raw `me`.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   {can('contracts_board', 'manage_job_state') && <CancelButton />}
 *
 * The backend is the security boundary (requirePermission enforces every route);
 * this only hides/disables controls the role can't use so the UI matches the API.
 * Superadmin resolves to the full key set server-side, so `can(...)` is always true.
 */
export function usePermissions() {
  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
    staleTime: 5 * 60 * 1000,
  });

  const permissions = me?.permissions ?? [];

  const can = (feature: string, action: string): boolean =>
    permissions.includes(`${feature}.${action}`);

  return { can, me, permissions, isLoading };
}
